/**
 * Toast notification system.
 *
 * useToast() — hook that returns { toasts, show, dismiss }.
 *   show(message, variant?) — pushes a toast; auto-dismisses after 3 s.
 *   dismiss(id)             — manually removes a toast.
 *
 * ToastContainer — renders the fixed overlay stack.
 *   Mount once per screen. Pass toasts + onDismiss from useToast().
 */

import { useCallback, useState } from 'react';
import {
  type ColorPalette,
  Icons, StatusDot,
  FONT_SIZE, RADIUS, SPACE, SIZE,
} from '@vigilo/design-system';

// ── Types ─────────────────────────────────────────────────────────────────────

export type ToastVariant = 'success' | 'warning' | 'error';

export interface ToastItem {
  id:      string;
  variant: ToastVariant;
  message: string;
}

// ── Hook ──────────────────────────────────────────────────────────────────────

export function useToast() {
  const [toasts, setToasts] = useState<ToastItem[]>([]);

  const dismiss = useCallback((id: string) => {
    setToasts(prev => prev.filter(t => t.id !== id));
  }, []);

  const show = useCallback((message: string, variant: ToastVariant = 'success') => {
    const id = Math.random().toString(36).slice(2, 10);
    setToasts(prev => [...prev, { id, variant, message }]);
    setTimeout(() => {
      setToasts(prev => prev.filter(t => t.id !== id));
    }, 3000);
  }, []);

  return { toasts, show, dismiss };
}

// ── Container ─────────────────────────────────────────────────────────────────

export function ToastContainer({ toasts, onDismiss, C }: {
  toasts:    ToastItem[];
  onDismiss: (id: string) => void;
  C:         ColorPalette;
}) {
  if (toasts.length === 0) return null;

  return (
    <div style={{
      position: 'fixed', bottom: SPACE[20], right: SPACE[20],
      display: 'flex', flexDirection: 'column', gap: SPACE[8],
      zIndex: 9999,
      fontFamily: "'Albert Sans', sans-serif",
    }}>
      {toasts.map(toast => {
        const color = toast.variant === 'success' ? C.green
                    : toast.variant === 'warning'  ? C.amber
                    : C.ember;
        return (
          <div key={toast.id} style={{
            display: 'flex', alignItems: 'center', gap: SPACE[10],
            padding: `${SPACE[10]}px ${SPACE[14]}px`,
            background: C.panelHi,
            border: `1px solid ${color}55`,
            borderLeft: `3px solid ${color}`,
            borderRadius: RADIUS.card,
            minWidth: 240, maxWidth: 360,
            boxShadow: '0 4px 16px rgba(0,0,0,0.3)',
          }}>
            <StatusDot color={color} size={6} />
            <span style={{ flex: 1, fontSize: FONT_SIZE.body, color: C.text }}>{toast.message}</span>
            <button
              onClick={() => onDismiss(toast.id)}
              style={{
                background: 'transparent', border: 'none',
                color: C.muted, cursor: 'pointer', padding: 0, flexShrink: 0,
              }}
            >
              <Icons.X size={SIZE.iconMd} />
            </button>
          </div>
        );
      })}
    </div>
  );
}
